'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';
import React from 'react';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { CTAButtonProps } from '@repo/ui/src/types';
import { classNames } from '@repo/ui/src/utils/classNames';
import useNumericId from '@repo/ui/src/hooks/useNumericId';
import Product from '@models/product';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';

const { locally } = publicRuntimeConfig;
interface Props {
  product?: Product;
  buttonProps?: CTAButtonProps;
}

const BuyOnlineButton = ({ product, buttonProps }: Props) => {
  const instanceId = useNumericId();
  const { t } = useTranslation();
  const { geo } = useCurrentUrl();
  const lang = geo?.locale.toLowerCase() || 'en-us';
  const isCompact = buttonProps?.size === 'small';
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isLocallyLoaded, setIsLocallyLoaded] = React.useState(false);

  const generateConfig = React.useCallback(() => {
    const isCanada = geo?.geoLoc?.includes('ca');
    return {
      id: String(instanceId),
      company_name: locally.companyName || 'Traeger Grill',
      button_text: t('productDetailPageContent.locallyButtonText', undefined, {
        default: 'Find it Locally',
      }),
      button_id: `HTML`,
      company_id: String(locally.companyId || '112256'),
      css: '3',
      color_0: '#D95E16',
      always_hide_button: '1',
      enable_buy_online_button: '1',
      buy_online_button_text: t('productDetailPageContent.buyOnlineButtonText', undefined, {
        default: 'Buy',
      }),
      n_buy_online_results: '3',
      upc: product?.shop?.upc ?? '',
      show_location_prompt: '1',
      lang: lang,
      n_dealers: '3',
      no_link: '1',
      default_tab: '1', // Set "Buy Online" as the default tab
      radius_in_miles: '25',
      ...(isCanada && { only_show_country: 'CA' }),
    };
  }, [
    instanceId,
    product?.shop?.upc,
    lang,
    t,
    locally.companyName,
    locally.companyId,
    geo?.geoLoc,
  ]);

  const generateUrl = React.useCallback((config: Record<string, any>) => {
    return new URLSearchParams(
      Object.entries(config).map(([key, value]) => [
        key,
        typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? ''),
      ]),
    );
  }, []);

  const injectScript = React.useCallback((src: string, scriptId: string) => {
    const existingScript = document.getElementById(scriptId);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = src;
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  React.useEffect(() => {
    const config = generateConfig();
    const url = generateUrl(config);
    const scriptId = `lcly-script-${instanceId}`;

    injectScript(`https://traeger-grill.locally.com/stores/map.js?${url}`, scriptId);

    // SwitchLive tracking scripts
    injectScript(
      `https://traeger-grill.locally.com/switchlive/switchlive-events.js?company_id=${locally.companyId}`,
      `lcly-script-switchlive-${instanceId}`,
    );
    injectScript(
      `https://traeger-grill.locally.com/js/switchlive.js?company_id=${locally.companyId}`,
      `lcly-script-switchlive-events-${instanceId}`,
    );
  }, [generateConfig, generateUrl, injectScript, product?.shop?.upc, lang, instanceId]);

  React.useEffect(() => {
    const checkLocallyButton = () => {
      const locallyButton = containerRef.current?.querySelector<HTMLAnchorElement>(
        `#lcly-button-${instanceId} a[id^="lcly-buy-online-button"]`,
      );
      if (locallyButton) {
        setIsLocallyLoaded(true);
        return true;
      }
      return false;
    };

    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      if (checkLocallyButton()) {
        observer.disconnect();
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    // Cleanup after 10 seconds or on unmount
    const timeoutId = setTimeout(() => observer.disconnect(), 10000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  const handleBuyOnlineClick = React.useCallback(() => {
    // Find the dynamically injected Locally buy-online button
    const locallyButton = containerRef.current?.querySelector<HTMLAnchorElement>(
      `#lcly-button-${instanceId} a[id^="lcly-buy-online-button"]`,
    );
    if (locallyButton) {
      locallyButton.click();
    }
  }, [instanceId]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'w-full shop-local-button-style-container [&>*]:!hidden [&>button]:!inline-flex',
        isCompact && 'shop-local-button-style--compact',
      )}
    >
      {isLocallyLoaded && (
        <Button
          onClick={handleBuyOnlineClick}
          variant={ButtonVariant.primary}
          size={isCompact ? 'small' : 'medium'}
          className="justify-center"
          full
        >
          {t('productDetailPageContent.buyOnlineButtonText', undefined, {
            default: 'Buy',
          })}
        </Button>
      )}
      <div
        id={`lcly-button-${instanceId}`}
        data-switchlive="true"
        data-switchlive-impression="true"
        data-switchlive-impression-id-PL="1"
        className={classNames(
          'shop-local-button-style hidden',
          isCompact && 'shop-local-button-style--compact',
        )}
      >
        <a
          id={`lcly-link-${instanceId}`}
          data-switchlive="true"
          data-switchlive-mode="auto"
          data-switchlive-id-PL="6"
          role="button"
          href="https://join.locally.com/for-brands"
          target="_blank"
        >
          Product Locator by Locally
        </a>
      </div>
    </div>
  );
};

export default BuyOnlineButton;
